import {useEffect, useRef, useState} from 'react';
import Hls from "hls.js";
import PlainControl from "../plainControl/PlainControl";
import "./AudioPlayer.css"
import {useClientLocale} from "../../context/client_locale";
import adaptiveSize from "../../utils/size";

const hlsMimeTypes = [
    "application/x-mpegurl",
    "application/vnd.apple.mpegurl",
];
const nativeHlsMimeType = "application/vnd.apple.mpegurl";

function isHlsSource(source, type) {
    return hlsMimeTypes.includes(type?.toLowerCase()) || source.toLowerCase().includes(".m3u8");
}

function canUseNativeSource(audio, source, type) {
    return !isHlsSource(source, type) || audio.canPlayType(nativeHlsMimeType);
}

export default function AudioPlayer(props) {
    const src = props.src
    const type = props.type
    const { strings } = useClientLocale();
    const audioRef = useRef(null);
    const [hasPlayed, setHasPlayed] = useState(false);
    const resolutionUnit = props.resolutionUnit;
    const spaceBetweenItem = adaptiveSize(resolutionUnit, 1)
    const verticalPadding = adaptiveSize(resolutionUnit, 4)

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio || !src) {
            return undefined;
        }

        const tryAutoPlay = async () => {
            try {
                await audio.play();
            } catch (err) {
                console.log("Autoplay blocked, waiting for user gesture...");
            }
        };

        setHasPlayed(false);

        if (canUseNativeSource(audio, src, type)) {
            audio.src = src;
            tryAutoPlay();
            return undefined;
        }

        if (Hls.isSupported()) {
            const hls = new Hls({
                enableWorker: true,
                lowLatencyMode: true,
                backBufferLength: 30,
            });
            hls.on(Hls.Events.MANIFEST_PARSED, tryAutoPlay);
            hls.loadSource(src);
            hls.attachMedia(audio);

            return () => {
                hls.destroy();
            };
        }

        console.error("HLS audio is not supported in this browser.");
        return undefined;
    }, [src, type]);

    function onPlay() {
        setHasPlayed(true);
    }

    function playerStateHandler() {
        audioRef.current.currentTime = 0;
        if (hasPlayed) {
            audioRef.current.pause();
        } else {
            audioRef.current.play();
        }
    }

    function againPlayHandler() {
        audioRef.current.currentTime = 0;

        try {
            audioRef.current.play();
        } catch(err) {
            console.log("play audio failed", err);
        }
    }

    return (
        <div>
            <div
                className="flex flex-row gap-8 justify-center"
                style={{
                    gap: `${spaceBetweenItem}px`,
                    paddingLeft: `${verticalPadding}px`,
                    paddingRight: `${verticalPadding}px`,
                }}
            >
                <PlainControl
                    className="flex-1"
                    resolutionUnit={resolutionUnit}
                    id="again-control"
                    onClick={againPlayHandler}
                >
                    {strings["again"]}
                </PlainControl>
                <PlainControl
                    className="flex-1"
                    resolutionUnit={resolutionUnit}
                    id="end-control"
                    onClick={playerStateHandler}
                >
                    {hasPlayed ? strings["end"] : strings["play"]}
                </PlainControl>
            </div>
            <audio
                ref={audioRef}
                onPlay={onPlay}
                muted={false}
            />
        </div>
    )
}
